import styles from './Patrollers.module.css';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Icon from '@mui/material/Icon';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';

import Group from '@mui/icons-material/Group';

import React from 'react';

import { useAuth } from '../../AuthContext';
import { getUser, getAllUsers, updateUser } from "../../api.js";

import { Filter } from '../Filter/Filter.jsx';
import { Patroller } from '../Patroller/Patroller.jsx';

export const Patrollers = ({ newPatroller, size }) => {
    const [ error, setError ] = React.useState(null);
    const [ loading, setLoading ] = React.useState(true);
    const [ patrollers, setPatrollers ] = React.useState([]);
    const [ currentUser, setCurrentUser ] = React.useState(null);
    const [ page, setPage ] = React.useState(0);
    const [ rowsPerPage, setRowsPerPage ] = React.useState(size || 10);
    const [ filters, setFilters ] = React.useState({ search: '', showUnverifiedEmail: false, showUnverifiedPhone: false, showMissingNspNumber: false });

    const { auth, token } = useAuth();

    const pageSize = size ? Math.min(rowsPerPage, size) : rowsPerPage;

    // MARK: Effects
    React.useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const user = await getUser(auth.currentUser.uid, token);

                setCurrentUser(user);
            } catch (error) {
                setError(error.message);
            }
        };

        fetchCurrentUser();
    }, [ auth.currentUser.uid, token ]);

    React.useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const patrollersData = await getAllUsers(token);

            if (patrollersData.error) {
                setError(patrollersData.error);

                setLoading(false);

                return;
            }

            setPatrollers(patrollersData.filter(user => user.uid !== auth.currentUser.uid));

            setLoading(false);
        };

        fetchData();
    }, [ auth.currentUser.uid, token ]);

    React.useEffect(() => {
        if (newPatroller) {
            setPatrollers(prev => {
                const updated = [ ...prev, newPatroller ];

                updated.sort((a, b) => a.displayName?.localeCompare(b.displayName));

                return updated;
            });
        }
    }, [ newPatroller ]);

    // Update rowsPerPage if size prop changes and current selection exceeds limit
    React.useEffect(() => {
        if (size && rowsPerPage > size) {
            setRowsPerPage(size);
            setPage(0);
        }
    }, [ size, rowsPerPage ]);

    const rowsPerPageOptions = React.useMemo(() => {
        const options = [ 10, 25, 50, 100 ];

        return size ? options.filter(option => option <= size) : options;
    }, [ size ]);

    // Memoize the sorted patrollers to avoid re-sorting on every render
    const sortedPatrollers = React.useMemo(() => {
        // patrollers are sorted when the API returns
        return patrollers.filter(user => {
            if (filters.search && filters.search.trim() !== '') {
                const searchLower = filters.search.toLowerCase();

                const matchesName = user.firstName?.toLowerCase().includes(searchLower) ||
                    user.lastName?.toLowerCase().includes(searchLower) ||
                    user.displayName?.toLowerCase().includes(searchLower);

                const matchesEmail = user.email?.toLowerCase().includes(searchLower);
                const matchesPhone = user.phoneNumber?.toLowerCase().includes(searchLower);
                const matchesNSP = String(user.nspNumber)?.toLowerCase().includes(searchLower);
                const matchesCertification = user.certification?.toLowerCase().includes(searchLower);
                const matchesRole = user.role?.toLowerCase().includes(searchLower);

                if (!(matchesName || matchesEmail || matchesPhone || matchesNSP || matchesCertification || matchesRole)) {
                    return false;
                }
            }

            if (filters.showUnverifiedEmail) {
                if (user.emailVerified) {
                    return false;
                }
            }

            if (filters.showUnverifiedPhone) {
                if (user.phoneVerified) {
                    return false;
                }
            }

            if (filters.showMissingNspNumber) {
                if (user.nspNumber && user.nspNumber.trim() !== '') {
                    return false;
                }
            }

            return true;
        });
    }, [ patrollers, filters ]);

    // Memoize the current page data
    const currentPageData = React.useMemo(() => {
        const pageIndex = page;

        return sortedPatrollers.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
    }, [ sortedPatrollers, page, pageSize ]);

    // Memoize pagination helpers
    const paginationInfo = React.useMemo(() => {
        const totalPages = Math.ceil(sortedPatrollers.length / pageSize);

        return { totalPages };
    }, [ sortedPatrollers.length, pageSize ]);

    // MARK: Handlers
    const handleOnSave = async (user) => {
        try {
            const updated = await updateUser(user, token);

            if (updated.error) {
                setError(updated.error);
            } else {
                const updatedPatrollers = patrollers.map(u => u.id === user.id ? user : u);

                setPatrollers(updatedPatrollers);
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(0); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({ search: '', days: [], onlyAvailable: false });
        setPage(0);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // MARK: Render
    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert
            severity="error"
            variant='filled'
            onClose={ () => setError(null) }
        >
            { error }
        </Alert>
    }

    return (
        <>
            <Filter
                filters={ filters }
                onFilterChange={ handleFilterChange }
                onClearFilters={ handleClearFilters }
            />

            <div className={ styles.container }>
                <div className={ styles.titleBar }>
                    <span>
                        <Icon component={ Group } sx={ { mr: 1 } } />
                        <Typography>All Patrollers</Typography>
                    </span>

                    { paginationInfo.totalPages > 1 && <TablePagination
                        component="div"
                        color="primary"
                        count={ sortedPatrollers.length }
                        page={ page }
                        onPageChange={ (event, value) => setPage(value) }
                        rowsPerPage={ rowsPerPage }
                        rowsPerPageOptions={ rowsPerPageOptions }
                        onRowsPerPageChange={ handleChangeRowsPerPage }
                    /> }
                </div>

                <div className={ styles.content }>
                    { currentPageData.map(f => {
                        return (
                            <Patroller key={ f.id } patroller={ f } userIsAdmin={ currentUser?.admin } onSave={ handleOnSave } />
                        );
                    }) }
                </div>

                { paginationInfo.totalPages > 1 && <div className={ styles.footer }>
                    <TablePagination
                        component="div"
                        color="primary"
                        count={ sortedPatrollers.length }
                        page={ page }
                        onPageChange={ (event, value) => setPage(value) }
                        rowsPerPage={ rowsPerPage }
                        rowsPerPageOptions={ rowsPerPageOptions }
                        onRowsPerPageChange={ handleChangeRowsPerPage }
                    />
                </div> }
            </div>
        </>
    );
};